import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography } from '../../../../core/components/molecules/Typography';
import { usePrintComponentStore } from '../../../../core/components/organism/PrintComponent/store/usePrintComponentStore';
import { getDisciplineIcon } from '../../../../core/utils/discipline';
import { Colors } from '../../../../core/models/disciplines';
import { useExerciseStore } from '../../stores/exercisesStore';
import { DnaIcon } from 'morpheus-ui';
import CardLoader from '../cardLoading';
import SectionHeader from '../sectionHeader';
import ContentCard from '../../../../core/components/organism/ContentCard';
import ContentLoading from '../../../../core/components/atoms/ContentLoading';
import InnerHTMLContainer from '../../../../core/components/molecules/InnerHTMLContainer';
import NewPrintComponent from '../../../../core/components/organism/PrintComponent/newPrintComponent';
import ExercisePaper from '../../../../core/components/templates/PrintPaper/exercises';
import InfiniteScroll from 'react-infinite-scroll-component';
import FilterModal from './components/filterModal';
import { useNewExerciseHook } from '../../hooks/useNewExercise';
import { useExerciseUrlFilters } from '../../hooks/useExerciseUrlFilters';
import { useNewSubjectStore } from '../../stores/newSubjectStore';
import { useDisciplinesAuxStore } from '../../stores/auxStore';
import { IResponseFavorite } from '../../hooks/useContentFavorite';
import DisciplinesEmptyState from '../EmptyState';
import getSubjectByFilter from '../../../../core/commom/getSubjectByFilter';
import { getModuleDescription } from '../../utils/contentHelper';
import { getModuleLabel } from '../../utils/contentHelper';
import { useNewModuleStore } from '../../stores/newModuleStore';
import { auxiliarStore } from '../../../Practice/stores/auxiliarStore';
import { useAccess } from '../../../../core/accessControl/useAccess';
import { openFreemiumUpsell } from '../../../../core/stores/freemiumUpsellStore';
import {
  useFinishExercise,
  useSetContentView,
} from '../../../../core/hooks/useContentView';
import { getViewedFromResponse } from '../../../../core/models/contentView';
import { IExercise } from '../../../../core/models/exercise';

interface IExerciseList {
  module: string;
  discipline: Colors;
  url: string;
}

export default function ExerciseList({ module, discipline, url }: IExerciseList) {
  const navigate = useNavigate();

  const { mutate, isPending, hasMore } = useNewExerciseHook();
  const { writeStoresToUrl } = useExerciseUrlFilters();
  const { selectedSubjects } = useNewSubjectStore();
  const { exercises, totalExercises, setExercises, page, setPage, isLoading, setIsLoading } = useExerciseStore();
  const { setHasApiCall } = usePrintComponentStore();
  const { openModal, setOpenModal } = useDisciplinesAuxStore();
  const { modules } = useNewModuleStore();
  const { areas } = auxiliarStore();
  const { isContentLocked } = useAccess();

  const { mutateAsync: finishExercise } = useFinishExercise();
  const { mutateAsync: setContentView } = useSetContentView();
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const currentModule = useMemo(() => {
    return modules.find((m) => m.slug === module);
  }, [modules, module]);

  const areaId = useMemo(() => {
    if (!areas?.areas || !currentModule) return undefined;
    const area = areas.areas.find((a) =>
      a.subareas.some((s) => s.id === currentModule.id)
    );
    return area?.id;
  }, [areas, currentModule]);

  const subareaId = currentModule?.id;

  useEffect(() => {
    if(module) {
      mutate({ module, operation: 'initial', page: 1 });
      setPage(1);
    }
  }, [selectedSubjects]);

  useEffect(() => {
    if (totalExercises && page > 1) {
      mutate({ module, operation: 'pagination', page });
    }
  }, [page]);

  useEffect(() => {
    if (typeof window?.MathJax !== 'undefined') {
      window.MathJax.typesetPromise();
    }
  });

  const callbackFav = (data: IResponseFavorite) => {
    const exerciseIndex = exercises.findIndex((it) => it._id === data.targetId);

    if(exerciseIndex !== -1) {
      exercises[exerciseIndex].favorite = !exercises[exerciseIndex].favorite;
      setExercises(exercises);
    }
    if(data.ok && module) {
      mutate({ module, operation: 'initial', page: 1 });
    }
  }

  function dispatchSetContentAsDoneEvent(id: string) {
    window.dispatchEvent(
      new CustomEvent('setContentAsDone', {
        detail: { contentId: id },
      })
    );
  }

  const onFinish = async (id: string, slug: string) => {
    if (!slug) return;

    const current = exercises.find((it) => it.id === id);

    // Exercício NÃO é toggle no backend: `finish` só marca (e registra resposta
    // errada). Para desmarcar usamos o endpoint genérico `content-views`.
    // `viewStatus.viewed` da resposta é a fonte da verdade para a UI.
    setLoadingId(id);
    try {
      const res = current?.done
        ? await setContentView({
            contentType: 'exercise',
            contentId: id,
            viewed: false,
            origin: 'free_browsing',
          })
        : await finishExercise({ slug, origin: 'free_browsing' });

      const viewed = getViewedFromResponse(res);

      // Para exercício, `done === false` renderiza como "errado" (X vermelho).
      // Ao desmarcar (viewed = false) queremos o estado neutro ("não feito"),
      // que no ContentCard corresponde a `done === undefined`.
      const nextDone = viewed ? true : undefined;

      const updatedExercises = exercises.map((it) =>
        it.id === id ? ({ ...it, done: nextDone } as IExercise) : it
      );
      setExercises(updatedExercises);

      if (viewed) dispatchSetContentAsDoneEvent(id);
    } finally {
      setLoadingId(null);
    }
  }

  useEffect(() => {
    if (!isPending && isLoading) {
      setIsLoading(false);
    }
  }, [isPending, isLoading, setIsLoading]);

  if ((isPending || isLoading) && page === 1) {
    return (
      <Fragment>
        {Array.from({ length: 10 }, (_, index) => (
          <CardLoader key={index} />
        ))}
      </Fragment>
    );
  }

  return (
    <div className='flex flex-col gap-lg'>
      <NewPrintComponent 
        type='exercise'
        isOpen={openModal === 'exercise-print'}
        onClose={() => {
          setHasApiCall(false);
          setOpenModal(undefined);
        }}
        title={getModuleLabel(module || '')}
        subtitle={getModuleDescription(module || '')}
      >
        <ExercisePaper 
          cols="split" 
          size="default" 
          color={(discipline as Colors) ?? 'interdisciplinar'}
          iconType='svg'
          icon={getDisciplineIcon(discipline || "") || DnaIcon}
          title={module ?? ""}
          module={discipline ?? ""}
        /> 
      </NewPrintComponent>
      <FilterModal
        isOpen={openModal === 'exercise-filter'}
        onClose={() => setOpenModal(undefined)}
        onSubmit={() => {
          setPage(1);
          writeStoresToUrl();
          mutate({ module, operation:'filter', page: 1 });
        }}
        subtitle={getModuleLabel(module || '')}
        slug={module}
      />
      {!exercises || exercises.length === 0 ? <DisciplinesEmptyState /> : (
        <Fragment>
          <SectionHeader hasTitle={false}>
            <div className='flex h-fit w-full flex-row items-center gap-lg xs:justify-between md:justify-end'>
              <Typography size='small'>
                {totalExercises || 0}{' '}
                {!exercises || !totalExercises || totalExercises <= 1
                  ? 'exercício'
                  : 'exercícios'}
              </Typography>
            </div>
          </SectionHeader>
          <InfiniteScroll
            dataLength={exercises.length}
            next={() => setPage(page + 1)}
            hasMore={hasMore}
            loader={<ContentLoading type='list' />}
            className='flex flex-col gap-lg bg-color-transparent'
            style={{ overflow: 'visible' }}
          >
            {exercises &&
              exercises.map((it, i) => {
                const locked = isContentLocked(it);
                return (
                <ContentCard
                  color={discipline}
                  key={i}
                  content='Exercício'
                  favorite={it.favorite}
                  icon='edit'
                  action={() =>
                    locked
                      ? openFreemiumUpsell()
                      : navigate(
                          url + `/assunto/${it.subjects[0].slug}/exercicio/${it.slug}`
                        )
                  }
                  // subject={it.subjects[0].title}
                  subject={getSubjectByFilter(it.subjects, selectedSubjects)}
                  id={it.id}
                  slug={it.slug}
                  done={it.done}
                  firstViewedAt={it.firstViewedAt}
                  // "Marcar exercício como concluído" desabilitado temporariamente.
                  // O ícone permanece visível, porém sem ação. Para reativar
                  // (implementação futura), reative as linhas abaixo:
                  // onDone={(id: string, slug: string) => onFinish(id, slug)}
                  // doneLoading={loadingId === it.id}
                  hasCallback
                  callback={(data) => callbackFav(data)}
                  locked={locked}
                >
                  <InnerHTMLContainer
                    data={it.statement}
                    type='contentCard'
                    align='left'
                    font='noto'
                    className='text-sm text-mono-color-darkest transition delay-100 ease-in-out group-hover:text-card-color-default'
                  />
                </ContentCard>
                );
              })}
          </InfiniteScroll>
        </Fragment>
      )}
    </div>
  );
}
